<template>
  <div class="chart-with-timeline">
    <AppChart :options="mergedOptions" />
    <TimelineDescription
      :events="selectedEvents"
      class="chart-with-timeline__descriptor"
    />
  </div>
</template>

<script>
import AppChart from '@@/common/stats/AppChart';
import { deepMerge } from '@/utils/object';
import TimelineDescription from './TimelineDescription';
import Timeline from './Timeline';
import { TIMELINE } from './constant';
import lang from '@/mixins/lang';

let timelineCreator = undefined;

export default {
  name: 'ChartWithTimeline',
  components: { AppChart, TimelineDescription },
  mixins: [lang],
  props: {
    options: {
      type: Object,
      required: true,
    },
    events: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chartBound: {
        right: null,
        left: null,
        top: null,
        bottom: null,
      },
      points: [],
      chartRenderer: {},
      selectedCategory: null,
      currentCrosshair: null,
    };
  },
  computed: {
    timelineOptions() {
      const vueThis = this;
      return {
        chart: {
          events: {
            load() {
              timelineCreator = new Timeline(this.renderer);
              vueThis.chartRenderer = this.renderer;
            },
            render() {
              vueThis.points = this.series[0].data;
              vueThis.chartBound = {
                left: this.plotLeft,
                top: this.plotTop,
                bottom: this.plotTop + this.plotHeight,
                right: this.axes[0].width + this.plotLeft,
              };
              vueThis.createTimeline();
            },
            redraw() {
              vueThis.createTimeline();
              if (!timelineCreator.selectedEventRect) return;
              //the timelineCreator's selectedEventRect will change after the new Timeline created on above line
              //therefor the assignment should be under the creation line
              const selectedEventRect = timelineCreator.selectedEventRect;
              vueThis.selectedCategory =
                selectedEventRect.element.dataset['category'];
              vueThis.addCrosshairToPoint(timelineCreator.selectedEventRect);
            },
          },
          height: TIMELINE.HEIGHT + 300,
          spacingBottom: TIMELINE.HEIGHT,
        },
        xAxis: {
          labels: {
            autoRotation: false,
          },
        },
        legend: {
          align: 'left',
          verticalAlign: 'top',
          borderWidth: 0,
        },
      };
    },
    mergedOptions() {
      return deepMerge(this.timelineOptions, this.options);
    },
    timelineBounds() {
      const marginFromChart = 50;
      const top = this.chartBound.bottom + marginFromChart;
      return {
        ...this.chartBound,
        top,
        bottom: top + TIMELINE.HEIGHT,
      };
    },
    pointWithEvents() {
      return this.points.filter(
        ({ category }) => this.getEventsByDate(category).length,
      );
    },
    selectedEvents() {
      if (!this.selectedCategory) return [];
      return this.getEventsByDate(this.selectedCategory);
    },
    formatedEvents() {
      return this.events.map(event => ({
        ...event,
        date: this.formatDate(event.date),
      }));
    },
  },
  watch: {
    formatedEvents() {
      this.createTimeline();
    },
  },
  methods: {
    setSelectedEventRect(point) {
      this.selectedCategory = point.element.dataset['category'];
      this.addCrosshairToPoint(point);
    },
    getEventsByDate(date) {
      return this.formatedEvents.filter(event => event.date === date);
    },
    addCrosshairToPoint(point) {
      if (!point) return;
      const pointBounds = point.getBBox();
      // Point radius is 5;
      const x = pointBounds.x + 2.5;
      const y = this.chartBound.top;
      const height = this.chartBound.bottom - y;
      if (this.currentCrosshair) {
        this.currentCrosshair.destroy();
      }
      const line = this.chartRenderer
        .path(['M', x, y, 'v', height, 'z'])
        .attr({
          'stroke': '#bbb',
          'stroke-width': 1,
          'stroke-dasharray': 5,
        })
        .add();
      this.currentCrosshair = line;
    },
    createTimeline() {
      timelineCreator.create(
        this.timelineBounds,
        this.pointWithEvents,
        this.setSelectedEventRect,
      );
    },
    formatDate(date) {
      if (this.lang === 'fa') {
        return date.format('jYYYY-jMM-jDD');
      } else {
        return date.format('YYYY-MM-DD');
      }
    },
  },
};
</script>

<style lang="scss">
.chart-with-timeline__descriptor {
  margin-bottom: $global-margin;
}
</style>
